/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { ControlDefinition } from "@anvilor/ngx-rui";
import { LegacyRuiControl } from "@anvilor/ngx-rui";
import { RuiStore } from "@anvilor/ngx-rui";
import { Observable } from "rxjs";
import { map } from "rxjs";
import { distinctUntilChangedJson } from "@anvilor/ngx-core-utils";
import { filterNullish } from "@anvilor/ngx-core-utils";

export const CONTROL_SURVEY_STATES = "anvilor_forms:form_states";

export interface SurveyStatesControlDefinition extends ControlDefinition {
  control: typeof CONTROL_SURVEY_STATES;
  main: ControlDefinition;
  post_submit?: ControlDefinition;
  pre_open?: ControlDefinition;
  post_close?: ControlDefinition;
}

/**
 * Control which orchestrates rendering different controls based on the form's
 * current state (whether it is before/after open/close, or whether they have
 * just submitted, etc.)
 */
// eslint-disable-next-line max-len
export class SurveyStatesControl extends LegacyRuiControl<SurveyStatesControlDefinition> {
  mainControl$: Observable<LegacyRuiControl>;
  postSubmitControl$: Observable<LegacyRuiControl | null>;
  preOpenControl$: Observable<LegacyRuiControl | null>;
  postCloseControl$: Observable<LegacyRuiControl | null>;

  constructor(store: RuiStore, id: string) {
    super(store, id);

    const makeControlSelector = (
      f: (d: SurveyStatesControlDefinition) => ControlDefinition | undefined
    ): Observable<LegacyRuiControl | null> =>
      this.rawDefinitionSelect((d) => f(d)).pipe(
        distinctUntilChangedJson(),
        map((definition) =>
          definition
            ? this.store.controls.addNewControl({ definition, parent_iid: this.iid })
            : null
        )
      );

    this.mainControl$ = makeControlSelector((d) => d.main).pipe(filterNullish());
    this.postSubmitControl$ = makeControlSelector((d) => d.post_submit);
    this.preOpenControl$ = makeControlSelector((d) => d.pre_open);
    this.postCloseControl$ = makeControlSelector((d) => d.post_close);
  }
}
